import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ProfileTab } from "enums/ProfileTabs";

import { ArrowForwardIos } from "@material-ui/icons";

import { useWorkerQuery } from "services/input-core/workers/get-worker";
import { useActivityItemsFromWorkerWithStack } from "services/input-core/activity-items/get-activity-items-from-worker-with-stacks";
import { useWorkItemsFromWorker } from "services/input-core/work-items/get-work-items-from-worker";
import { useWorkersByCWEntityID } from "services/input-core/workers/get-all-workers-by-cw-entity-id";
import { useSOWQuery } from "services/input-core/sows/get-sow-by-id";

import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";
import { TabContent, TabSystem } from "components/UI/TabSystem";
import Header from "./components/Header";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import SOWsRelated from "./components/SOWsRelated/SOWsRelated";
import { WorkerRelatedTable } from "components/Tables/WorkerRelatedTable";

import GeneralTab, { BuildDetailsSection } from "./tabs/GeneralTab/GeneralTab";
import ActivityItemsTab from "./tabs/ActivityItemsTab/ActivityItemsTab";

import { tryRetrieveDateFromUrlParams } from "helpers/DateHelper";

import { useSubscription } from "store/auth/auth-store";
import {
  Line,
  StyledParagraph,
  StyledParagraphValue,
} from "./tabs/GeneralTab/GeneralTab.styles";
import { converterParaString, formatDateIsoUtc } from "utils/functions";
import { toast } from "react-toastify";

import { useActivityItemContext } from "store/activityItem";
import { useWorkerStore } from "store/workers";
import {
  WorkerProfile as WorkerProfileType,
  WorkItem,
} from "services/input-core/workers/types";

const WorkerProfile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("worker-profile");
  const { isBeltrame, isAngloAmericaBrazil } = useSubscription();
  const { setWorkerProfile, setWorkItemsFromWorker } = useWorkerStore();
  const [searchParams] = useSearchParams();
  const workerId = searchParams.get("id");

  const { data, isLoading } = useWorkerQuery(workerId, {
    onSuccess: (response: WorkerProfileType) => {
      setWorkerProfile(response);
    },
    onError: (error: any) => {
      if (error?.response?.data?.statusCode === 404) {
        navigate("*");
        toast.error(t("danger-worker-not-found", { ns: "list-messages" }));
        return;
      }

      if (error?.response?.data?.statusCode === 403) {
        navigate("/app/access-denied");
        toast.error(t("access-denied-message", { ns: "list-messages" }));
        return;
      }
    },
  });
  const [date, setDate] = useState<Date>(() => tryRetrieveDateFromUrlParams());

  const {
    setActivityItems,
    setLoading: setLoadingActivity,
    setRefetching: setRefetchingActivity,
  } = useActivityItemContext();

  const {
    data: activityItemsQuery,
    isLoading: isActivityItemsLoadingQuery,
    refetch: refetchActivityItems,
    isRefetching: isActivityItemsRefetchingQuery,
    isInitialLoading: isActivityItemsInitialLoading,
  } = useActivityItemsFromWorkerWithStack(data?.workerID, date, {
    enabled: !isLoading,
  });

  const {
    data: workItems,
    isLoading: isWorkItemsLoading,
    refetch: refetchWorkItems,
  } = useWorkItemsFromWorker(data?.workerID, date, undefined, {
    onSuccess: (response: WorkItem[]) => {
      setWorkItemsFromWorker(response);
    },
    enabled: (isBeltrame || isAngloAmericaBrazil) && !isLoading,
  });

  const { data: sows, isFetching: isLoadingSows } = useSOWQuery(
    data?.sowID,
    "operative",
    {
      enabled: !!data?.sowID,
      onError: (error: any) => {
        if (error?.response?.data?.status === 404) {
          navigate("*");
        }
      },
    }
  );

  const {
    data: workersFromConsolidatedWorker,
    isLoading: isLoadingWorkersFromConsolidatedWorker,
  } = useWorkersByCWEntityID(data?.consolidatedWorkerRef, true, {
    enabled: !isLoading && !!data?.consolidatedWorkerRef,
  });

  useEffect(() => {
    setLoadingActivity(isActivityItemsLoadingQuery);
    setRefetchingActivity(isActivityItemsRefetchingQuery);
    if (activityItemsQuery) setActivityItems(activityItemsQuery);
  }, [
    activityItemsQuery,
    isActivityItemsLoadingQuery,
    isActivityItemsRefetchingQuery,
    setActivityItems,
    setLoadingActivity,
    setRefetchingActivity,
  ]);

  const workersFormatted = (data: any) =>
    data?.map(
      (worker: { workerID: string; firstName: string; lastName: string }) => ({
        code: worker.workerID,
        name: `${worker.firstName + " "}${worker.lastName}`,
        email: "",
        actions: (
          <div className="actions-right">
            <ButtonTS
              justIcon
              round
              color="info"
              size="sm"
              onClick={() =>
                navigate(
                  `/app/workers/profile?profile=worker"&id=${worker.workerID}`
                )
              }
            >
              <ArrowForwardIos />
            </ButtonTS>
          </div>
        ),
      })
    ) || [];

  const tabs = [
    {
      id: ProfileTab.GENERAL,
      title: t("General", { ns: "profile" }),
    },
    {
      id: ProfileTab.ACTIVITY_ITEMS,
      title: t("Activity Items", { ns: "profile" }),
    },
    {
      id: ProfileTab.SOWS_RELATED,
      title: t("Sows Related", { ns: "profile" }),
    },
  ];

  const renderPrimaryDetails = useMemo(() => {
    const primaryDetailsArray: { text: string; value: any }[] = [];

    if (data?.workerID) {
      primaryDetailsArray.push({
        text: t("Worker ID", { ns: "profile" }),
        value: data?.workerID,
      });
    }

    if (data?.firstName && data?.lastName) {
      primaryDetailsArray.push({
        text: t("Worker Name", { ns: "profile" }),
        value: `${data?.firstName} ${data?.lastName}`,
      });
    }

    if (data?.sowWorkerRole?.name) {
      primaryDetailsArray.push({
        text: t("Worker Role", { ns: "profile" }),
        value: `${data?.sowWorkerRole?.name ?? " - "}`,
      });
    }

    if (data?.workerEmail) {
      primaryDetailsArray.push({
        text: t("E-mail", { ns: "profile" }),
        value: data?.workerEmail,
      });
    }

    return primaryDetailsArray;
  }, [data]);

  const renderSecondaryDetails = useMemo(() => {
    const secondaryDetailsArray: { text: string; value: any }[] = [];
    if (data?.vendorNumber && data?.vendorName) {
      secondaryDetailsArray.push({
        text: t("Belongs to Supplier", { ns: "profile" }),
        value: `${data?.vendorNumber} ${data?.vendorName}`,
      });
    }

    if (data?.workOrderWorkOrderRevisionOwner) {
      secondaryDetailsArray.push({
        text: t("Profile Worker Primary Contact", { ns: "profile" }),
        value: data?.workOrderWorkOrderRevisionOwner,
      });
    }

    if (data?.siteName) {
      secondaryDetailsArray.push({
        text: t("Site", { ns: "profile" }),
        value: data?.siteName,
      });
    }

    if (data?.siteCode) {
      secondaryDetailsArray.push({
        text: t("Location", { ns: "profile" }),
        value: data?.siteCode,
      });
    }

    if (data?.businessUnitName && data?.businessUnitCode) {
      secondaryDetailsArray.push({
        text: t("Business Unit", { ns: "profile" }),
        value: `${data?.businessUnitName} ${data?.businessUnitCode}`,
      });
    }

    if (data?.costCenterName) {
      secondaryDetailsArray.push({
        text: t("Cost Center", { ns: "profile" }),
        value: data?.costCenterName,
      });
    }

    if (data?.workOrderWorkOrderRevisionOwner) {
      secondaryDetailsArray.push({
        text: t("Supervisor", { ns: "profile" }),
        value: data?.workOrderWorkOrderRevisionOwner,
      });
    }

    return secondaryDetailsArray;
  }, [data]);

  const workerBuildDetails: BuildDetailsSection[] = [
    {
      title: t("Worker Details", { ns: "profile" }),
      items: renderPrimaryDetails,
    },
    {
      title: t("Secondary Details", { ns: "profile" }),
      items: renderSecondaryDetails,
    },
    {
      title: t("Custom Fields", { ns: "profile" }),
      items: [
        {
          text: t("Fields for Uverify", { ns: "profile" }),
          value: (
            <div>
              {data?.customFields?.map(
                (field: { name: string; value: any }, index: number) => {
                  let displayValue = field.value;
                  if (field.name === "Date of birth") {
                    displayValue = formatDateIsoUtc(field.value);
                  }

                  return (
                    <Line key={index}>
                      <StyledParagraph>
                        {t(field?.name, { ns: "custom-fields" })}:
                      </StyledParagraph>
                      <StyledParagraphValue>
                        {converterParaString(displayValue, (text: string) =>
                          t(text, { ns: "profile" })
                        )}
                      </StyledParagraphValue>
                    </Line>
                  );
                }
              )}
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <GridContainer>
      <GridItemTS xs={12}>
        <Header
          data={{
            fieldglassId: data?.workerID,
            name: data?.firstName + " " + data?.lastName,
            status: data?.status,
            zIdentifier: data?.workerUpload,
            startDate: data?.startDate,
            endDate: data?.workerEndDate,
            supplierName: data?.vendorName,
            supplierCode: data?.vendorNumber,
          }}
          isLoading={isLoading}
          type="worker"
        />
      </GridItemTS>

      <GridItemTS xs={12} style={{ marginTop: "1rem" }}>
        <TabSystem tabs={tabs} syncWithUrlParams>
          <TabContent value={ProfileTab.GENERAL}>
            <GeneralTab
              isLoading={isLoading}
              buildDetails={workerBuildDetails}
            />
          </TabContent>

          <TabContent value={ProfileTab.ACTIVITY_ITEMS}>
            <ActivityItemsTab
              fieldglassId={data?.workerID}
              entityDate={{ start: data?.startDate, end: data?.workerEndDate }}
              date={date}
              setDate={setDate}
              isActivityItemsInitialLoading={isActivityItemsInitialLoading}
              refetchActivityItems={refetchActivityItems}
              workItems={workItems}
              isWorkItemsLoading={isWorkItemsLoading}
              refetchWorkItems={refetchWorkItems}
              profile={"WORKER"}
              supplierCode={data?.vendorNumber}
            />
          </TabContent>

          <TabContent value={ProfileTab.SOWS_RELATED}>
            <SOWsRelated
              sows={sows === undefined ? [] : [sows]}
              isLoading={isLoadingSows}
              isSowAdm={true}
            />
          </TabContent>

          <TabContent value={ProfileTab.WORKERS_RELATED}>
            <WorkerRelatedTable
              data={
                workersFromConsolidatedWorker
                  ? workersFormatted(workersFromConsolidatedWorker)
                  : []
              }
              isLoading={isLoadingWorkersFromConsolidatedWorker}
              omit={["email", "vendorName", "vendorCode", "numberDNI"]}
              initialRowsPerPage={5}
            />
          </TabContent>
        </TabSystem>
      </GridItemTS>
    </GridContainer>
  );
};

export default WorkerProfile;
